import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Accordion,
  Alert,
  Box,
  HStack,
  Separator,
  Text,
  VStack,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useCurrencies } from 'hooks';
import { useTranslation } from 'localization';
import { LDUser, LDUsers } from 'types';

import { SERVICE_CARD_BORDER_RADIUS } from 'components';
import { formatLdDisplayName } from 'utils';

import InitForm from '../InitForm';

type LDListProps = { lds: LDUsers };

const LDList: React.FC<LDListProps> = ({ lds }) => {
  const { t } = useTranslation('component.withdraw.ld');

  const [searchParams] = useSearchParams();
  const [activeLD, setActiveLD] = React.useState<null | LDUser>(null);

  const { data: currencies } = useCurrencies({
    queries: {},
    enabled: Boolean(lds?.length),
  });

  dayjs.extend(utc);
  const now = dayjs.utc();

  return (
    <Box w={{ base: '100%' }}>
      <Text color="body2" fontWeight="medium" mb={3}>
        {t('chooseLd')}
      </Text>
      <Accordion.Root
        collapsible
        display="flex"
        flexDir={{ base: 'column', lg: 'row' }}
        flexWrap="wrap"
        gap={{ base: 0, lg: 4 }}
        onValueChange={(details: { value: string[] }) => {
          const id = details.value[0];
          if (id && lds) {
            setActiveLD(lds.find((l) => l.id === id) ?? null);
          } else {
            setActiveLD(null);
          }
        }}
      >
        {lds?.map((ld) => {
          const ldDisplayName = formatLdDisplayName(
            ld.ld_contract.nick_name,
            t('depositorFallback')
          );

          const today = ld.ld_contract.working_hours.find(
            (daily) => daily.day === dayjs().get('d')
          );
          const isActiveToday = today?.is_open;
          const openTime = today?.open_time || '00:00';
          const closeTime = today?.close_time || '23:59';

          const startWorkingHour = dayjs(
            now.format(`YYYY-MM-DDT${openTime}:00+00:00`)
          );
          const endWorkingHour = dayjs(
            now.format(`YYYY-MM-DDT${closeTime}:00+00:00`)
          );

          const isActiveWorkingHour =
            now.diff(endWorkingHour, 'minute') < 0 &&
            now.diff(startWorkingHour, 'minute') > 0;

          const payCurrencyOptions =
            currencies?.list
              ?.filter((c) => ld.ld_contract.pay_out_currencies.includes(c.id))
              .map((c) => ({
                label: c.title,
                value: c.symbol,
                icon: c.icon,
              })) || [];

          return (
            <Box key={ld.id} w={{ base: '100%', lg: '45%' }} py={4}>
              <Accordion.Item
                value={ld.id}
                borderWidth="1px"
                borderRadius={SERVICE_CARD_BORDER_RADIUS}
                overflow="hidden"
                bg="secondary"
                borderColor={
                  !isActiveToday || !isActiveWorkingHour
                    ? 'rgba(255,62,58,0.4)'
                    : activeLD?.id === ld.id
                      ? 'rgba(255,255,255,0.14)'
                      : 'rgba(255,255,255,0.08)'
                }
              >
                <Accordion.ItemTrigger px={4} py={3}>
                  <HStack w="100%">
                    <Text fontWeight="bold" textAlign="left" color="body">
                      {ldDisplayName}
                    </Text>
                    <Text fontSize="xs" textAlign="left" color="body2">
                      {'( ' + ld.country.cca3 + ' )'}
                    </Text>
                  </HStack>
                  <Accordion.ItemIndicator />
                </Accordion.ItemTrigger>
                <Accordion.ItemContent pb={0}>
                  <Accordion.ItemBody p={0}>
                    <Box
                      w="100%"
                      bg="dark"
                      borderTopWidth="1px"
                      borderTopColor="rgba(255,255,255,0.08)"
                      px={{ base: 4, md: 6 }}
                      py={{ base: 5, md: 6 }}
                      borderBottomRadius="lg"
                    >
                      <VStack w="100%" align="stretch" gap={5}>
                        <HStack w="100%" justify="space-between" gap={3}>
                          <Text color="body2">{t('ldNumber')}</Text>
                          <Text color="body">{ld.identifier_number}</Text>
                        </HStack>
                        <Separator borderColor="rgba(255,255,255,0.08)" />
                        {isActiveToday && isActiveWorkingHour ? (
                          <Box w="100%">
                            <InitForm
                              currencyList={payCurrencyOptions}
                              ldUsers={lds}
                              activeLD={activeLD}
                              walletId={searchParams.get('wallet') || ''}
                            />
                          </Box>
                        ) : (
                          <Alert.Root
                            colorPalette="yellow"
                            borderRadius="md"
                            w="100%"
                            px={4}
                            py={3}
                          >
                            <Text color="warning">{t('isNotWorking')}</Text>
                          </Alert.Root>
                        )}
                      </VStack>
                    </Box>
                  </Accordion.ItemBody>
                </Accordion.ItemContent>
              </Accordion.Item>
            </Box>
          );
        })}
      </Accordion.Root>
    </Box>
  );
};

export default LDList;
